/**
 * Owners repository — the data-access home for the `owners` slice.
 *
 * The ONLY place that touches `owners` data (`supabase.from("owners")` during
 * the Supabase phase, the Nest client after the flip). Hooks (`useCreateOwner`,
 * `useOwnerDetails`, `useUpdateOwner`, `useDeactivateOwner`) and components call
 * these functions; nothing else may call `supabase.from("owners")` directly.
 *
 * Strangler-Fig per-slice migration (manifest-6, migration-a3-owners):
 *   Step 1 (done) — consolidated scattered `supabase.from("owners")` here.
 *   Step 2 (done) — guarded NestJS endpoints (`apps/api/src/owners/*`).
 *   Step 3 (HERE) — the staff CRUD methods are flipped to the Nest client; the
 *                   owner-portal self-service reads (which use OTHER code paths,
 *                   not this repo) stay on Supabase/RLS.
 *
 * ── Flip scope (owner-vs-staff authz split) ──────────────────────────────────
 *   FLIPPED to Nest (with a Supabase fallback): `create`, `getById`, `update`,
 *     `deactivate` — all reached only by the staff dashboard (AddOwnerDialog /
 *     EditOwnerDialog / OwnerDetails via useOwners), whose callers hold
 *     `owners.{view,create,edit}`.
 *   KEPT on Supabase (documented):
 *     - `count` — a dashboard-stats projection with no matching Nest endpoint
 *        (the route group is getById/create/update/deactivate only). Stays on
 *        Supabase until a stats endpoint exists.
 *     - The owner *portal* self-service reads (`useOwnerData`, `OwnerAuth`,
 *        `src/pages/portal/owner/*`) use DIFFERENT code paths entirely and are
 *        RLS-gated for the owner's own record — flipping them to the guarded
 *        `owners` endpoints would 403 the owner (no `owners.*` key). Not in this
 *        repo, not flipped.
 *
 * Each flipped method tries Nest first (only when an API is configured) and
 * falls back to the original Supabase query on any error — so this is a
 * reversible unit and local dev without the Nest API keeps working. RLS is
 * untouched. Nest responses are parsed through the shared `ownerSchema` (a
 * too-strict DTO would silently fall back to Supabase — the #1699 lesson).
 *
 * Convention audit note:
 * - `owners` has `is_active` (soft-deactivation) AND `deleted_at`, but the
 *   established owners semantics use `is_active = false` for soft deletion.
 * - `deactivate()` sets `is_active = false` — this is the soft-delete for owners.
 * - A hard `.delete()` is intentionally NOT used — it would destroy FK
 *   references from properties, contracts, and maintenance_requests.
 * - Every read/write is company-scoped.
 *
 * Deferred (NOT migrated here): the cross-domain `supabase.from("owners")`
 * reads in hooks/useOwnerData, hooks/useFinance, hooks/useRerImport,
 * repositories/invoices, pages/OwnerAuth, pages/super-admin/Users, plus the
 * `owners_with_status` view list and `owner_payouts` reads — see the deferred
 * backlog (domain-debugging-matrix "read-by" column).
 */
import { API_ROUTES } from '@/api/routes';
import { supabase } from '@/integrations/supabase/client';
import type { Tables, TablesUpdate } from '@/integrations/supabase/types';
import {
  isDefinitiveNotFound,
  reportNestFallback,
  reportNestFallbackOrRethrowAuth,
  reportNestFallbackOrRethrowUnknownCommit,
} from '@/lib/nestFallback';
import type { OwnerEntityMeta } from '@/lib/ownerHelpers';
import { API_URL } from '@/services/api';
import { makeNestRequest, NestRequestError } from '@/services/nestRequest';
import { ownerSchema } from '@dallali/shared';

const TABLE = 'owners';

/**
 * Narrow per-owner entity metadata returned by {@link ownersRepository.getEntityMeta}.
 * The required-nullable view of {@link OwnerEntityMeta} (same fields; never
 * `undefined`), so the two shapes can't drift independently.
 */
export type OwnerEntityMetaRow = Required<OwnerEntityMeta>;

// Authenticated Nest request bound to the "owners" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
const nestRequest = makeNestRequest('owners');

// Row-scoped variant: forwards x-active-role for staff READ endpoints (ADR-0001 P1).
// The backend RowScopeGuard uses this header to enforce per-role row filters and
// genuinely 403s without it, so `requireActiveRole` short-circuits the boot-race
// window client-side (issue #3278) — see src/services/nestRequest.ts.
// WRITE endpoints use the unscoped `nestRequest` above (writes are perm-gated only).
const nestRequestScoped = makeNestRequest('owners', {
  forwardActiveRole: true,
  requireActiveRole: true,
});

type OwnerInsertPayload = {
  company_id: string;
  full_name: string;
  user_id?: string | null;
  full_name_en?: string | null;
  national_id?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  is_active?: boolean;
  invitation_status?: string;
  owner_type?: 'individual' | 'commercial';
  commercial_name?: string | null;
  commercial_name_en?: string | null;
  unified_number?: string | null;
  cr_number?: string | null;
  vat_number?: string | null;
  representative_name?: string | null;
  representative_name_en?: string | null;
  representative_id_number?: string | null;
  representative_phone?: string | null;
  date_of_birth?: string | null;
};

// The Supabase queries below carry the precise `owners` Row typing the
// dashboard consumes. The Nest path returns the SAME row, validated at runtime
// by the shared `ownerSchema`; we cast the parsed row back to the
// Supabase-inferred type so consumers see no type drift across the flip.
type OwnerRow = Tables<'owners'>;

/**
 * One row from `get_owners_with_stats` — every `owners_with_status` column plus
 * the four aggregated stats. Mirrors the function's RETURNS TABLE shape.
 */
export type OwnerWithStatsRow = OwnerRow & {
  status: string;
  properties_count: number;
  units_count: number;
  total_income: number;
  total_fees: number;
};

// ── Supabase implementations (fallback for the flipped methods, and the live
//    path for the methods kept on Supabase) ────────────────────────────────────

/** Digits-only, mirroring the Nest owners.repository + the DB index expression. */
function digitsOnly(v: string | null | undefined): string | null | undefined {
  return typeof v === 'string' ? v.replace(/[^0-9]/g, '') : v;
}

async function createViaSupabase(
  payload: OwnerInsertPayload
): Promise<OwnerRow> {
  // Store establishment identifiers DIGITS-NORMALIZED so this fallback matches the
  // Nest create() path AND the owners_company_{cr,unified}_number_unique index
  // expression — no raw-store path remains (C4 · #3038). The Nest path normalizes
  // server-side; here (Supabase-direct) we normalize before insert.
  const normalized: OwnerInsertPayload = {
    ...payload,
    ...(payload.cr_number != null && {
      cr_number: digitsOnly(payload.cr_number),
    }),
    ...(payload.unified_number != null && {
      unified_number: digitsOnly(payload.unified_number),
    }),
  };
  const { data, error } = await supabase
    .from(TABLE)
    .insert(normalized)
    .select()
    .single();
  if (error) throw error;
  return data as OwnerRow;
}

async function getByIdViaSupabase(id: string, companyId: string) {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .eq('company_id', companyId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

async function updateViaSupabase(
  id: string,
  patch: TablesUpdate<'owners'>,
  companyId: string
) {
  const { data, error } = await supabase
    .from(TABLE)
    .update(patch)
    .eq('id', id)
    .eq('company_id', companyId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

async function deactivateViaSupabase(id: string, companyId: string) {
  const { error } = await supabase
    .from(TABLE)
    .update({ is_active: false })
    .eq('id', id)
    .eq('company_id', companyId);
  if (error) throw error;
}

/**
 * Projection returned by `findByPhone` — just the display-name columns the
 * phone-lookup consumer (`useContactNameByPhone`) needs. Keep this narrow:
 * callers needing more columns should add a new projection, not widen this one.
 */
export interface OwnerPhoneContact {
  full_name: string | null;
  commercial_name: string | null;
}

/** Narrow owner contact row for the task property-context sidebar (fe-tail T9). */
export interface TaskContextOwnerRow {
  id: string;
  full_name: string;
  full_name_en: string | null;
  phone: string | null;
  email: string | null;
}

async function findByPhoneViaSupabase(
  companyId: string,
  candidates: string[],
  limit: number
): Promise<OwnerPhoneContact[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('full_name, commercial_name')
    .eq('company_id', companyId)
    .in('phone', candidates)
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as OwnerPhoneContact[];
}

/**
 * Like `findByPhone` but resolves MANY phones in one round-trip and includes
 * `phone` so the caller can key each result back to its conversation — the
 * Conversations inbox resolves a whole page of phones → names at once.
 */
export interface OwnerPhoneContactRow extends OwnerPhoneContact {
  phone: string | null;
  full_name_en: string | null;
  commercial_name_en: string | null;
}
async function findManyByPhonesViaSupabase(
  companyId: string,
  candidates: string[],
  limit: number
): Promise<OwnerPhoneContactRow[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select(
      'phone, full_name, full_name_en, commercial_name, commercial_name_en'
    )
    .eq('company_id', companyId)
    .in('phone', candidates)
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as OwnerPhoneContactRow[];
}

export const ownersRepository = {
  /**
   * Owner SELF-creation at signup / dashboard auto-import via the SECURITY
   * DEFINER RPC `create_owner_for_signup`. Kept in the repository so the hook
   * never touches `owners` data directly. Unlike {@link create} (a staff op
   * routed through Nest), this is owner-role self-service: the owner lacks
   * `owners.create` (Nest → 403) and a direct insert is RLS-denied, so the RPC
   * (running as definer, asserting auth.uid()===userId) is the only path. It is
   * idempotent (relink-by-national_id or insert), links the caller's COMPLETED
   * Nafath verification, and syncs contact server-side. Returns the owner id.
   */
  async createOwnerForSignup(params: {
    userId: string;
    companyId: string;
    fullName?: string | null;
    nationalId: string;
  }): Promise<string | null> {
    const { data, error } = await supabase.rpc('create_owner_for_signup', {
      p_user_id: params.userId,
      p_company_id: params.companyId,
      // Optional args (SQL DEFAULT NULL): pass undefined → omitted → SQL NULL.
      p_full_name: params.fullName || undefined,
      p_national_id: params.nationalId,
    });
    if (error) throw error;
    return (data as string | null) ?? null;
  },

  /**
   * Insert an owner (payload must already carry `company_id`); returns the row.
   *
   * Staff op — flipped to Nest (POST /owners; company_id resolved server-side
   * from the JWT, the body value ignored), Supabase fallback. The Nest response
   * is the inserted row, validated through the shared `ownerSchema`.
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399), layered UNDER
   * the pre-existing reviewed custom guard below (DALLALI-NESTJS-API-G): a
   * deterministic non-auth 4xx (e.g. 409 duplicate-owner) still rethrows
   * immediately, before either guard's fallback logic runs — falling back would
   * just re-hit the same constraint. 401/403 still fall back to Supabase — they
   * are "safe" 4xx under the write-safe guard too (no commit could have
   * happened), so that path is unchanged. What changes: an AMBIGUOUS outcome
   * (network, timeout, 5xx, parse failure after a 2xx) now RETHROWS instead of
   * re-issuing the insert through Supabase — a fallback after a
   * committed-but-lost Nest write would double-create the owner.
   */
  async create(payload: OwnerInsertPayload): Promise<OwnerRow> {
    // ── Dedupe (owner fragmentation fix) ─────────────────────────────────────
    // One owner record per (national_id, company). If a person with this
    // national_id already exists in the company, REUSE that record instead of
    // inserting a second one — a duplicate would split the person's properties
    // across owner rows and hide them from the owner portal (which shows one
    // owner record's properties). Reactivates a soft-deactivated match. Scoped to
    // the company, so a person who owns in several companies still gets one row
    // per company (expected). Skipped when no national_id is supplied.
    if (payload.national_id) {
      const { data: dupes } = await supabase
        .from(TABLE)
        .select('id, is_active')
        .eq('company_id', payload.company_id)
        .eq('national_id', payload.national_id)
        .order('created_at', { ascending: true })
        .limit(1);
      const existing = dupes?.[0];
      if (existing) {
        if (!existing.is_active) {
          await this.update(
            existing.id,
            { is_active: true },
            payload.company_id
          ).catch(() => {
            /* best-effort reactivate — must not gate the reuse */
          });
        }
        const full = await this.getById(existing.id, payload.company_id);
        if (full) return full as OwnerRow;
      }
    }

    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.owners.create(), {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        return ownerSchema.parse(row) as unknown as OwnerRow;
      } catch (err) {
        // A deterministic 4xx is Nest's REAL answer (e.g. 409 owner-already-
        // exists). Falling back to Supabase would just re-hit the same global
        // unique constraint, and a nest-fallback report would be noise — surface
        // the conflict so the mutation shows the server's bilingual message.
        // (DALLALI-NESTJS-API-G / -FRONTEND-AJ+B7: a duplicate-phone owner-create
        // stormed prod with 500s + Supabase fallbacks.)
        //
        // EXCEPT 401/403: an auth/config failure (e.g. a missing/rotated
        // VITE_API_KEY, or an expired session) is exactly when the Supabase
        // safety net should still engage — keep falling back for those rather
        // than hard-failing the create.
        if (
          err instanceof NestRequestError &&
          err.kind === 'http' &&
          err.status !== undefined &&
          err.status >= 400 &&
          err.status < 500 &&
          err.status !== 401 &&
          err.status !== 403
        ) {
          throw err;
        }
        reportNestFallbackOrRethrowUnknownCommit('owners.create', err);
      }
    }
    return createViaSupabase(payload);
  },

  /**
   * Single owner by id, company-scoped. Returns null if not found.
   *
   * Staff op — flipped to Nest (GET /owners/:id), Supabase fallback. The Nest
   * response is the bare row, validated through the shared `ownerSchema`.
   *
   * Row-scoped (ADR-0001 P1): uses nestRequestScoped which forwards the
   * x-active-role header so the backend RowScopeGuard enforces per-role row
   * filters — an owner can only fetch their own record, an employee can only
   * fetch owners of their assigned properties.
   */
  async getById(id: string, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequestScoped<unknown>(
          API_ROUTES.owners.getById(id)
        );
        return (row
          ? ownerSchema.parse(row)
          : null) as unknown as OwnerRow | null;
      } catch (err) {
        reportNestFallback('owners.getById', err);
      }
    }
    return getByIdViaSupabase(id, companyId);
  },

  /**
   * Update a company-scoped owner; returns the updated row (or null if not found).
   *
   * The `patch` is typed as `TablesUpdate<"owners">` for the typed columns.
   * Callers that pass un-typed columns should cast their patch as needed.
   *
   * Staff op — flipped to Nest (PUT /owners/:id), Supabase fallback. The Nest
   * response is the updated row, validated through the shared `ownerSchema`.
   */
  async update(id: string, patch: TablesUpdate<'owners'>, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.owners.update(id), {
          method: 'PUT',
          body: JSON.stringify(patch),
        });
        return (row
          ? ownerSchema.parse(row)
          : null) as unknown as OwnerRow | null;
      } catch (err) {
        reportNestFallback('owners.update', err);
      }
    }
    return updateViaSupabase(id, patch, companyId);
  },

  /**
   * Soft-deactivate a company-scoped owner (`is_active = false`).
   *
   * The `owners` table uses `is_active` (not `deleted_at`) for soft deletion,
   * matching the tenants/employees/service_providers pattern in this schema.
   * A hard `.delete()` is intentionally NOT used here — it would destroy FK
   * references from properties, contracts, and maintenance_requests.
   *
   * Staff op — flipped to Nest (PUT /owners/:id/deactivate), Supabase fallback.
   */
  async deactivate(id: string, companyId: string) {
    if (API_URL) {
      try {
        await nestRequest<unknown>(API_ROUTES.owners.deactivate(id), {
          method: 'PUT',
        });
        return;
      } catch (err) {
        reportNestFallback('owners.deactivate', err);
      }
    }
    return deactivateViaSupabase(id, companyId);
  },

  /**
   * Company-scoped owner count (dashboard stats).
   *
   * KEPT on Supabase — there is no matching Nest endpoint (the route group is
   * getById/create/update/deactivate only). Stays here until a stats endpoint
   * is added.
   *
   * Goes through the `get_owners_count` SECURITY DEFINER RPC (NOT a direct
   * `.from("owners")` HEAD/count read) — see migration 20260702120000, same
   * reasoning as `getEntityMeta` above: a direct count still pays the
   * per-row "Active-role view owners" RLS cost (the `employee` branch's
   * correlated `get_employee_property_ids()` calls), which timed out for
   * large-portfolio companies (Sentry DALLALI-FRONTEND-4B, called
   * unconditionally from `useUsersStats`).
   */
  async count(companyId: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any).rpc('get_owners_count', {
      p_company_id: companyId,
    });
    if (error) throw error;
    // PostgREST serialises the RPC's bigint return as a string.
    return Number(data ?? 0);
  },

  /**
   * Look up display-name columns for any owner whose `phone` matches one of
   * `candidates`, scoped to the company. Used by `useContactNameByPhone` to
   * resolve a name shown beside the phone in the messages list — historical
   * rows exist in several phone shapes (canonical `966XXXXXXXXX`, `+966X`,
   * local `05X`, bare 9-digit), so the caller generates every plausible
   * variant and we pass them all through PostgREST's `IN(...)`.
   *
   * KEPT on Supabase — there is no Nest endpoint for this projection (the
   * `owners` route group is getById/create/update/deactivate only). Returns
   * a narrow `{ full_name, commercial_name }[]` — do NOT widen this projection
   * without a corresponding caller need.
   */
  async findByPhone(
    companyId: string,
    candidates: string[],
    limit = 50
  ): Promise<OwnerPhoneContact[]> {
    return findByPhoneViaSupabase(companyId, candidates, limit);
  },
  async findManyByPhones(
    companyId: string,
    candidates: string[],
    limit = 200
  ): Promise<OwnerPhoneContactRow[]> {
    return findManyByPhonesViaSupabase(companyId, candidates, limit);
  },

  /**
   * Resolve every `owners.id` belonging to the same *person* within a company.
   *
   * Resolution strategy:
   * 1. If the owner has a `national_id`, find ALL rows (active or soft-deleted)
   *    within the company sharing that national_id.
   * 2. Also union any rows matching the owner's `user_id` (catches fragments
   *    where national_id is NULL — e.g. a name-only admin-created row or a
   *    dedup-loser whose national_id was cleared).
   * 3. Falls back to `[ownerId]` if neither national_id nor user_id is available.
   */
  async resolvePersonIds(
    ownerId: string,
    companyId: string
  ): Promise<string[]> {
    const { data: owner, error: ownerErr } = await supabase
      .from(TABLE)
      .select('national_id, user_id')
      .eq('id', ownerId)
      .eq('company_id', companyId)
      .maybeSingle();

    if (ownerErr) throw ownerErr;
    if (!owner) return [ownerId];

    const ids = new Set<string>([ownerId]);

    if (owner.national_id) {
      const { data: byNid, error: byNidErr } = await supabase
        .from(TABLE)
        .select('id')
        .eq('company_id', companyId)
        .eq('national_id', owner.national_id);
      if (byNidErr) throw byNidErr;
      byNid?.forEach((r) => ids.add(r.id));
    }

    if (owner.user_id) {
      const { data: byUid, error: byUidErr } = await supabase
        .from(TABLE)
        .select('id')
        .eq('company_id', companyId)
        .eq('user_id', owner.user_id);
      if (byUidErr) throw byUidErr;
      byUid?.forEach((r) => ids.add(r.id));
    }

    return [...ids];
  },

  /**
   * Find an owner within a company by national_id.
   * KEPT on Supabase — simple existence check, no matching Nest endpoint.
   */
  async getByNationalId(nationalId: string, companyId: string) {
    // phone/email are returned so signup-linking callers (useRerImport) can
    // fill blanks from the authenticated identity — 2026-06-12 blank-phone fix.
    const { data, error } = await supabase
      .from(TABLE)
      .select('id, user_id, is_active, phone, email')
      .eq('company_id', companyId)
      .eq('national_id', nationalId)
      .maybeSingle();

    if (error) throw error;

    // Backfill is_active on a previously-deactivated row so the guard
    // reuses (never duplicates) an admin-deactivated-then-re-encountered row.
    // Fire-and-forget: a transient update failure must not gate the return.
    if (data && !data.is_active) {
      this.update(data.id, { is_active: true }, companyId).catch((err) => {
        reportNestFallback('owners.getByNationalId.backfill', err);
      });
    }

    return data;
  },

  /**
   * Find an owner within a company by user_id.
   * KEPT on Supabase — simple existence check, no matching Nest endpoint.
   */
  async getByUserId(userId: string, companyId: string) {
    const { data, error } = await supabase
      .from(TABLE)
      .select('id')
      .eq('company_id', companyId)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Contact/identity card for the receipt detail (party block on the receipt
   * UI/PDF), company-scoped. KEPT on Supabase — enrichment sub-read of
   * `useReceipt`. NEVER throws: the pre-consolidation site destructured
   * `{ data }` only, so a failed read renders the receipt without the owner
   * card instead of erroring the whole receipt query.
   */
  async getReceiptContact(id: string, companyId: string) {
    const { data } = await supabase
      .from(TABLE)
      .select(
        'full_name, full_name_en, phone, email, address, national_id, owner_type, commercial_name, commercial_name_en, representative_name, representative_name_en, cr_number, unified_number, vat_number'
      )
      .eq('id', id)
      .eq('company_id', companyId)
      .maybeSingle();
    return data;
  },

  /**
   * Active owners as `{ id, full_name }` for the owner-payouts aggregation.
   * KEPT on Supabase — batched finance read. NEVER throws: the
   * pre-consolidation site collapsed error and empty into the same
   * empty-payouts result, so an error resolves to [].
   */
  async listActiveIdName(
    companyId: string
  ): Promise<{ id: string; full_name: string }[]> {
    const { data } = await supabase
      .from(TABLE)
      .select('id, full_name')
      .eq('company_id', companyId)
      .eq('is_active', true);
    return data ?? [];
  },

  /**
   * An owner's name + phone, company-scoped (task-report WhatsApp share).
   * NEVER throws: the pre-consolidation site destructured `{ data }` only
   * (share falls back to the no-phone toast), so an error resolves to null.
   */
  async getNamePhone(
    id: string,
    companyId: string
  ): Promise<{ full_name: string | null; phone: string | null } | null> {
    const { data } = await supabase
      .from(TABLE)
      .select('full_name, phone')
      .eq('id', id)
      .eq('company_id', companyId)
      .maybeSingle();
    return (data as { full_name: string | null; phone: string | null } | null) ?? null;
  },

  /**
   * Single owner with Nafath-verification status, company-scoped.
   *
   * Returns every `owners` column plus derived fields:
   *   status: 'verified' | 'not_verified' | 'not_invited'
   *   nafath_verified: boolean
   *   nafath: matched nafath_verifications row (null when not verified)
   *
   * FLIPPED to Nest (GET /owners/:id/status; compute-in-service pattern,
   * byte-identical to the `owners_with_status` Supabase view — manifest-9
   * Track-E). Supabase loud-fallback: on any Nest error the view path
   * continues to serve the shape unchanged.
   *
   * Row-scoped (ADR-0001 P1): uses nestRequestScoped which forwards the
   * x-active-role header so the backend RowScopeGuard enforces per-role
   * row filters.
   */
  async getWithStatus(id: string, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequestScoped<unknown>(
          API_ROUTES.owners.getByIdWithStatus(id)
        );
        return ownerSchema.parse(row) as unknown as
          | (OwnerRow & {
              status: 'verified' | 'not_verified' | 'not_invited';
              nafath_verified: boolean;
              nafath: Record<string, unknown> | null;
            })
          | null;
      } catch (err) {
        // Deterministic 404 = the owner is genuinely absent / soft-deleted /
        // cross-tenant / row-scoped out — Nest's real answer (#3407). For the
        // absent/cross-tenant/row-scoped cases the Supabase view fallback
        // (company_id filter only) would return the same null; for a
        // soft-deleted owner Nest is the post-flip authority even if the view
        // (defined in the infra repo) still exposes the row. Either way:
        // answer null directly, no fallback round-trip, no noise event.
        if (isDefinitiveNotFound(err)) return null;
        // owners_with_status is security_invoker=true — re-throw auth errors so
        // the Supabase fallback isn't called with no session (42501).
        reportNestFallbackOrRethrowAuth('owners.getWithStatus', err);
      }
    }

    // ── Supabase fallback (view path — kept until view is dropped) ──────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any)
      .from('owners_with_status')
      .select('*')
      .eq('id', id)
      .eq('company_id', companyId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    const owner = data as OwnerRow & {
      status?: string;
      user_id?: string | null;
    };
    const nafathVerified = owner.status === 'verified';

    // Fetch the matched nafath_verifications row for single-owner reads.
    let nafathRow: Record<string, unknown> | null = null;
    if (nafathVerified && owner.user_id && owner.national_id) {
      const { data: nv } = await supabase
        .from('nafath_verifications')
        .select('*')
        .eq('user_id', owner.user_id)
        .eq('national_id', owner.national_id)
        .eq('company_id', companyId)
        .eq('status', 'COMPLETED')
        .maybeSingle();
      nafathRow = nv as Record<string, unknown> | null;
    }

    return {
      ...(data as OwnerRow),
      nafath_verified: nafathVerified,
      nafath: nafathRow,
    };
  },

  /**
   * Owners list with per-owner aggregated stats, company-scoped, newest-first.
   *
   * KEPT on Supabase — backed by the `get_owners_with_stats` SECURITY DEFINER
   * aggregate (migration 20260608120000), NOT a Nest endpoint. Replaces the old
   * browser-side fan-out (a ~90-element `owner_id IN (...)` on `properties` with
   * an embedded `units` join) that timed out under RLS on large companies and
   * hung the Owners tab on prod. The aggregate runs in one GROUP BY query and
   * re-asserts the tenant/permission guard internally.
   *
   * If you roll back the migration, revert the `useOwners` change in the same
   * train — the hook calls this method directly (no Supabase fallback path).
   */
  async listWithStats(companyId: string): Promise<OwnerWithStatsRow[]> {
    // The RPC's return shape isn't in the generated Supabase types yet.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any).rpc(
      'get_owners_with_stats',
      {
        p_company_id: companyId,
      }
    );
    if (error) throw error;
    // PostgREST serialises the aggregate's bigint/numeric stats as strings;
    // coerce here so OwnerWithStatsRow's `number` typing holds for every caller.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data ?? []) as any[]).map((r) => ({
      ...r,
      properties_count: Number(r.properties_count),
      units_count: Number(r.units_count),
      total_income: Number(r.total_income),
      total_fees: Number(r.total_fees),
    })) as OwnerWithStatsRow[];
  },

  /**
   * Narrow per-company projection of owner ENTITY metadata (type +
   * establishment + representative names), keyed by owner id. Powers the
   * individual-vs-organization display in owner pickers without widening the
   * heavier listWithStats shape. Active owners only; company-scoped.
   *
   * Goes through the `get_owner_entity_meta` SECURITY DEFINER RPC (NOT a
   * direct `.from("owners")` read) — see migration 20260702120000. A direct
   * read is filtered per-row by the "Active-role view owners" RLS policy,
   * whose `employee` branch calls `get_employee_property_ids()` as a
   * correlated subquery up to twice per row; for companies with a
   * non-trivial owners table that blows the `authenticated` role's 8s
   * statement_timeout and PostgREST surfaces the cancellation as a raw 500
   * (Sentry DALLALI-FRONTEND-DA / DALLALI-FRONTEND-4B). The RPC re-asserts
   * the tenant/permission guard internally, exactly like
   * `get_owners_with_stats` (listWithStats above).
   */
  async getEntityMeta(
    companyId: string
  ): Promise<Record<string, OwnerEntityMetaRow>> {
    const map: Record<string, OwnerEntityMetaRow> = {};
    // The RPC's return shape isn't in the generated Supabase types yet.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any).rpc(
      'get_owner_entity_meta',
      {
        p_company_id: companyId,
      }
    );
    if (error) throw error;
    for (const r of (data ?? []) as Array<
      { id: string } & OwnerEntityMetaRow
    >) {
      map[r.id] = {
        owner_type: r.owner_type ?? null,
        commercial_name: r.commercial_name ?? null,
        commercial_name_en: r.commercial_name_en ?? null,
        representative_name: r.representative_name ?? null,
        representative_name_en: r.representative_name_en ?? null,
      };
    }
    return map;
  },

  /**
   * The narrow owner contact row for the task property-context sidebar
   * (fe-tail T9). NEVER throws — the inline original destructured `{ data }`
   * only, so an error renders the card without the owner section.
   */
  async taskContextOwner(
    ownerId: string,
    companyId: string,
  ): Promise<TaskContextOwnerRow | null> {
    const { data } = await supabase
      .from(TABLE)
      .select('id, full_name, full_name_en, phone, email')
      .eq('id', ownerId)
      .eq('company_id', companyId)
      .maybeSingle();
    return (data as TaskContextOwnerRow | null) ?? null;
  },
};
